import { createContext, use } from 'react'

import type { LtiLinkSettings } from '../activity'

/** An LTI link activity's own settings as the editor holds them: custom parameters as typed, one `name=value` a line. */
export interface EditedLtiLink {
  readonly toolId: string | null
  readonly targetLinkUri: string
  readonly customParameters: string
  readonly requiresScoreForCompletion: boolean
}

export function editedLtiLink(link: LtiLinkSettings): EditedLtiLink {
  return {
    toolId: link.toolId ?? null,
    targetLinkUri: link.targetLinkUri ?? '',
    customParameters: Object.entries(link.customParameters)
      .map(([name, value]) => `${name}=${value}`)
      .join('\n'),
    requiresScoreForCompletion: link.requiresScoreForCompletion,
  }
}

/** A new LTI link: no tool until one is chosen. */
export function newLtiLink(): EditedLtiLink {
  return { toolId: null, targetLinkUri: '', customParameters: '', requiresScoreForCompletion: false }
}

/**
 * The custom parameters as typed, by name. A value is everything after the first `=`, spaces
 * included, so a saved value round-trips unchanged; a line with no `=` is a name with an empty
 * value, so the API names a bad one.
 */
export function customParametersOf(text: string): Readonly<Record<string, string>> {
  return Object.fromEntries(
    text
      .split(/\r?\n/)
      .filter((line) => line.trim() !== '')
      .map((line) => {
        const at = line.indexOf('=')
        return at < 0 ? [line.trim(), ''] : [line.slice(0, at).trim(), line.slice(at + 1)]
      }),
  )
}

/** What a save sends for an LTI link. */
export function ltiLinkBody(link: EditedLtiLink): Record<string, unknown> {
  return {
    toolId: link.toolId,
    targetLinkUri: link.targetLinkUri.trim() === '' ? null : link.targetLinkUri.trim(),
    customParameters: customParametersOf(link.customParameters),
    requiresScoreForCompletion: link.requiresScoreForCompletion,
  }
}

/**
 * The tool picker slot in the LTI link editor, which Launch fills with its picker of the tools the
 * author's organization may use (LCH-15). A contribution reads and sets the link's tool through
 * `useLtiToolPicker`.
 */
export const ltiToolPickerSlot = 'catalog.activity-editor.lti-tool-picker'

/** The link the tool picker is for: its tool's public id (`ltt_…`), and a way to set it. */
export interface LtiToolPicker {
  readonly toolId: string | null
  readonly choose: (toolId: string | null) => void
}

export const LtiToolPickerContext = createContext<LtiToolPicker | null>(null)

/** The LTI link whose tool the surrounding picker slot sets. */
export function useLtiToolPicker(): LtiToolPicker {
  const picker = use(LtiToolPickerContext)
  if (picker === null) {
    throw new Error('useLtiToolPicker is for the tool picker slot of an LTI link editor.')
  }
  return picker
}
